import React, { useState, useEffect } from 'react';
import { useUsers } from './UsersContext';
import { T, inputCls, inputStyle, onFocus, onBlur, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn, statusBadge, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const AdminYoutubeTab = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [viewUser, setViewUser] = useState(null);
  const [search, setSearch] = useState('');
  const [viewReview, setViewReview] = useState(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-getAll`, { method: 'GET' });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      const allVideos = (parsed.data || []).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setVideos(allVideos);
      setCategories([...new Set(allVideos.map(v => v.category).filter(Boolean))]);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchVideos(); }, [refresh]);

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const toggleSelect = id => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === videos.length ? [] : videos.map(v => v.content_id));

  const handleBulkAction = async (action, comments = '') => {
    if (selectedIds.length === 0) { showMsg('Select at least one item', 'error'); return; }
    if ((action === 'PUBLIC' || action === 'PRIVATE') && videos.some(v => selectedIds.includes(v.content_id) && v.status !== 'APPROVED')) { showMsg('Cannot change visibility for non-approved content', 'error'); return; }
    if (action === 'REJECT' && !comments) { setRejectModal(true); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      const reviewerName = localStorage.getItem('vrvName') || localStorage.getItem('vrvEmail') || '';
      let allSuccess = true;
      for (const content_id of selectedIds) {
        const payload = { content_id, action };
        if (action === 'APPROVE') { payload.reviewedBy = reviewerName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = 'APPROVED'; }
        if (action === 'REJECT') { payload.reviewedBy = reviewerName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = comments; }
        const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
        if (!res.ok) allSuccess = false;
      }
      showMsg(allSuccess ? `${action} applied to ${selectedIds.length} item(s)` : 'Some actions failed', allSuccess ? 'success' : 'error');
      setSelectedIds([]); fetchVideos(); triggerRefresh();
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const filteredVideos = videos.filter(v => (!filterCategory || v.category === filterCategory) && (search === '' || Object.values(v).some(val => String(val).toLowerCase().includes(search.toLowerCase()))));

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="flex-1"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className={inputCls} style={{ ...inputStyle, width: 'auto' }} onFocus={onFocus} onBlur={onBlur}>
          <option value="">All Categories</option>
          {categories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
        </select>
      </div>

      {loading && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>}

      {!loading && videos.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="desktop-table">
            <TableCard>
              <TableBar className="table-bar-wrap">
                <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${videos.length} items`}</p>
                <div className="flex flex-wrap gap-2">
                  {[['Public', 'gold'], ['Private', 'muted'], ['Approve', 'green'], ['Reject', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                    <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
                  ))}
                </div>
              </TableBar>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr>
                    <th className="px-3 py-3 w-8" style={{ backgroundColor: T.stripe, borderBottom: `1px solid ${T.border}` }}><input type="checkbox" checked={selectedIds.length === videos.length && videos.length > 0} onChange={toggleSelectAll} /></th>
                    <Th>Title</Th><Th>Category</Th><Th>Created By</Th><Th>URL</Th><Th>Date</Th><Th>Status</Th><Th>Visibility</Th><Th center>Review</Th>
                  </tr></thead>
                  <tbody>
                    {filteredVideos.map((video, i) => (
                      <Tr key={video.content_id || i} selected={selectedIds.includes(video.content_id)}>
                        <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(video.content_id)} onChange={() => toggleSelect(video.content_id)} /></td>
                        <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{video.title || '—'}</td>
                        <td className="px-3 py-2.5" style={{ color: T.muted }}>{video.category}</td>
                        <td className="px-3 py-2.5">{video.createdBy ? <button onClick={() => { const u = getUserObj(video.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline" style={{ color: T.gold }}>{getUserName(video.createdBy) || '...'}</button> : '—'}</td>
                        <td className="px-3 py-2.5"><a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate block max-w-[150px]" style={{ color: T.dark }}>{video.youtube_url}</a></td>
                        <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{video.createdAt ? new Date(video.createdAt).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={statusBadge(video.status)}>{video.status || '—'}</span></td>
                        <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={visibilityBadge(video.visibility)}>{video.visibility}</span></td>
                        <td className="px-3 py-2.5 text-center">{video.reviewedBy ? <button onClick={() => setViewReview(video)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.gold }}>View</button> : '—'}</td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TableCard>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards">
            <div className="flex flex-wrap gap-2 mb-3">
              {[['Public', 'gold'], ['Private', 'muted'], ['Approve', 'green'], ['Reject', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
              ))}
            </div>
            {filteredVideos.map((video, i) => (
              <div key={video.content_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(video.content_id) ? `1.5px solid ${T.gold}` : undefined }}>
                <div className="flex items-start gap-3 mb-2">
                  <input type="checkbox" checked={selectedIds.includes(video.content_id)} onChange={() => toggleSelect(video.content_id)} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{video.title || '—'}</p>
                    <p className="text-xs" style={{ color: T.muted }}>{video.category} · {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : '—'}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest flex-shrink-0" style={statusBadge(video.status)}>{video.status || '—'}</span>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs mb-3">
                  <div><span style={{ color: T.muted }}>By: </span>{video.createdBy ? <button onClick={() => { const u = getUserObj(video.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(video.createdBy) || '...'}</button> : '—'}</div>
                  <div><span style={{ color: T.muted }}>Visibility: </span><span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase" style={visibilityBadge(video.visibility)}>{video.visibility}</span></div>
                </div>
                <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <a href={video.youtube_url} target="_blank" rel="noopener noreferrer"><ActionBtn color="muted" small>URL</ActionBtn></a>
                  {video.reviewedBy && <ActionBtn onClick={() => setViewReview(video)} color="gold" small>Review</ActionBtn>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {viewUser && <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUser} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div></Modal>}
      {viewReview && (
        <Modal onClose={() => setViewReview(null)} title="Review Details" subtitle="Review Info">
          {[['Reviewed By', viewReview.reviewedBy], ['Reviewed At', viewReview.reviewedAt ? new Date(viewReview.reviewedAt).toLocaleString() : '—'], ['Comments', viewReview.reviewComments || 'No comments']].map(([label, val]) => (
            <div key={label} className="mb-3">
              <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: T.muted }}>{label}</p>
              <p className="text-sm font-medium px-3 py-2 rounded-lg whitespace-pre-wrap" style={{ backgroundColor: T.stripe, color: T.ink, border: `1px solid ${T.border}` }}>{val}</p>
            </div>
          ))}
          <div className="mt-4"><PrimaryBtn onClick={() => setViewReview(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}
      {rejectModal && <RejectModal value={rejectComment} onChange={e => setRejectComment(e.target.value)} onCancel={() => { setRejectModal(false); setRejectComment(''); }} onConfirm={() => { handleBulkAction('REJECT', rejectComment || 'Rejected by admin'); setRejectModal(false); setRejectComment(''); }} />}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminYoutubeTab;
